import { Link } from "react-router-dom";
import { TopBar } from "../components/Layout";

const BASE = "/brand/logo-concepts";

interface Concept {
  n: number;
  name: string;
  rationale: string;
  recommended?: boolean;
}

const CONCEPTS: Concept[] = [
  {
    n: 1,
    name: "AC Ligature",
    rationale:
      "A monogram “A” cradled inside an open “C” arc — initials plus embrace. Ownable and " +
      "institution-grade; the most classically “logo-y” option.",
  },
  {
    n: 2,
    name: "Node Arrow",
    rationale:
      "A small network whose connecting edges form an implied upward arrow. The most literal " +
      "expression of “connect,” with the arrow carrying “action.”",
  },
  {
    n: 3,
    name: "Support Spark",
    rationale:
      "A chat/support bubble with a forward play arrow and a teal motion spark. The only concept " +
      "that unmistakably signals helpdesk. Runner-up.",
  },
  {
    n: 4,
    name: "Linked Chevrons",
    rationale:
      "Two interlocking chevrons that read at once as a chain link (connect) and a fast-forward " +
      "glyph (action) — both ideas fused into one shape. Most scalable and one-color-robust.",
    recommended: true,
  },
  {
    n: 5,
    name: "Orbit Pulse",
    rationale:
      "A forward chevron with a connection node orbiting on a ring — “a system in motion.” " +
      "Dynamic and distinctive; great as an animated loading mark.",
  },
];

const PALETTE = [
  { hex: "#5b8cff", label: "Indigo (primary)" },
  { hex: "#7c5cff", label: "Violet" },
  { hex: "#2dd4bf", label: "Teal (accent)" },
  { hex: "#0b1020", label: "Deep navy" },
  { hex: "#121a33", label: "Navy surface" },
  { hex: "#e7ecf7", label: "Light text" },
];

export function ResourceLogosPage() {
  return (
    <>
      <TopBar crumbs={<><Link to="/resources">Resources</Link> / Logo &amp; brand assets</>} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Logo &amp; brand assets</h1>
            <div className="sub">
              Five logo directions for ActionConnect, all on the brand palette. Recommended:{" "}
              <strong>Linked Chevrons</strong>. Every asset is pure SVG — no raster, no remote fonts.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Brand palette</h2>
          </div>
          <div className="card-body">
            <div className="swatch-row">
              {PALETTE.map((p) => (
                <div key={p.hex} className="swatch">
                  <span className="swatch-chip" style={{ background: p.hex }} />
                  <span className="swatch-label">
                    {p.label}
                    <code>{p.hex}</code>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {CONCEPTS.map((c) => {
          const mark = `${BASE}/concept-${c.n}-mark.svg`;
          const lockup = `${BASE}/concept-${c.n}-lockup.svg`;
          const lockupDark = `${BASE}/concept-${c.n}-lockup-dark.svg`;
          return (
            <div className="card" key={c.n}>
              <div className="card-head">
                <h2>
                  {String(c.n).padStart(2, "0")} — {c.name}{" "}
                  {c.recommended && <span className="rec-tag">Recommended</span>}
                </h2>
                <div className="btn-row">
                  <a className="btn small" href={mark} download>
                    Mark
                  </a>
                  <a className="btn small" href={lockup} download>
                    Lockup
                  </a>
                  <a className="btn small" href={lockupDark} download>
                    Lockup (dark)
                  </a>
                </div>
              </div>
              <div className="card-body">
                <p className="muted" style={{ marginTop: 0 }}>
                  {c.rationale}
                </p>
                <div className="logo-previews">
                  <figure className="logo-tile light">
                    <div className="mark-sizes">
                      <img src={mark} alt={`${c.name} mark 64px`} width={64} height={64} />
                      <img src={mark} alt={`${c.name} mark 32px`} width={32} height={32} />
                      <img src={mark} alt={`${c.name} mark 16px`} width={16} height={16} />
                    </div>
                    <figcaption>Mark on light</figcaption>
                  </figure>
                  <figure className="logo-tile dark">
                    <div className="mark-sizes">
                      <img src={mark} alt={`${c.name} mark on navy`} width={64} height={64} />
                      <img src={mark} alt={`${c.name} mark on navy 32px`} width={32} height={32} />
                      <img src={mark} alt={`${c.name} mark on navy 16px`} width={16} height={16} />
                    </div>
                    <figcaption>Mark on navy</figcaption>
                  </figure>
                  <figure className="logo-tile light wide">
                    <img className="lockup" src={lockup} alt={`${c.name} lockup`} height={40} />
                    <figcaption>Lockup</figcaption>
                  </figure>
                  <figure className="logo-tile dark wide">
                    <img className="lockup" src={lockupDark} alt={`${c.name} lockup on navy`} height={40} />
                    <figcaption>Lockup on navy</figcaption>
                  </figure>
                </div>
              </div>
            </div>
          );
        })}

        <p className="muted" style={{ fontSize: "0.85rem" }}>
          Every mark also works as a one-color version — replace the gradient/teal fills with a single{" "}
          <code>currentColor</code> for print, embossing, or low-ink contexts. Source files live in the
          repo under <code>brand/logo-concepts/</code>.
        </p>
      </div>
    </>
  );
}
